import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, isMobileBreakpoint } from '@theme/utilities';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';
const REQUIRED_AVAILABILITY_FILTER = 'filter.v.availability';
const REQUIRED_AVAILABILITY_VALUE = '1';
const PRODUCT_TAG_FILTER = 'filter.p.tag';
const FILTER_SETTING_GROUP_PREFIX = 'gid://shopify/FilterSettingGroup/';

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];
  #scrollAfterDialogClose = false;
  /** @type {Promise<boolean> | undefined} */
  #pendingSectionRender;
  #filterRequestId = 0;
  #resultsScrollId = 0;
  #queuedResultsScrollId = 0;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleCollectionNavigationClick);
    window.addEventListener('pagehide', this.#cancelResultsScroll);
    this.updateCollectionNavigationLinks();
    this.renderUnavailableActiveFilters();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleCollectionNavigationClick);
    window.removeEventListener('pagehide', this.#cancelResultsScroll);
    this.#cancelResultsScroll();
  }

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    const formParameters = new URLSearchParams(/** @type any */ (formData));
    const newParameters = new URLSearchParams(window.location.search);

    for (const element of this.refs.facetsForm.elements) {
      if ('name' in element && typeof element.name === 'string' && element.name) {
        newParameters.delete(element.name);
      }
    }

    const quickFilterInputs = document.querySelectorAll('[data-quick-filter-input][name]');
    for (const input of quickFilterInputs) {
      if (input instanceof HTMLInputElement) newParameters.delete(input.name);
    }

    for (const [param, value] of formParameters) {
      newParameters.append(param, value);
    }

    const activeQuickFilter = document.querySelector('[data-quick-filter-input]:checked');
    if (activeQuickFilter instanceof HTMLInputElement) {
      newParameters.set(activeQuickFilter.name, activeQuickFilter.value);
    }

    newParameters.delete('section_id');
    newParameters.delete(REQUIRED_AVAILABILITY_FILTER);
    newParameters.set(REQUIRED_AVAILABILITY_FILTER, REQUIRED_AVAILABILITY_VALUE);

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    this.#normalizeProductTagFilter(newParameters);

    return newParameters;
  }
  /**
   * Returns the selected values for a filter parameter.
   * @param {URLSearchParams} urlParameters - The URL parameters to read
   * @param {string} paramName - The filter parameter name
   * @returns {string[]} The selected values
   */
  #getFilterValues(urlParameters, paramName) {
    const values = urlParameters.getAll(paramName);
    return paramName === PRODUCT_TAG_FILTER ? values.flatMap((value) => value.split(',')).filter(Boolean) : values;
  }

  /**
   * Encodes all selected product tags as one comma-separated parameter.
   * @param {URLSearchParams} urlParameters - The URL parameters to normalize
   */
  #normalizeProductTagFilter(urlParameters) {
    const values = [...new Set(this.#getFilterValues(urlParameters, PRODUCT_TAG_FILTER))];

    urlParameters.delete(PRODUCT_TAG_FILTER);
    if (values.length > 0) urlParameters.set(PRODUCT_TAG_FILTER, values.join(','));
  }

  #isFacetsParameter(param) {
    return param.startsWith('filter.') || param === 'sort_by' || param === 'page' || param === 'section_id';
  }

  #mergeURLParameters(urlParameters) {
    const mergedParameters = new URLSearchParams(window.location.search);

    for (const param of new Set(mergedParameters.keys())) {
      if (this.#isFacetsParameter(param)) mergedParameters.delete(param);
    }

    const preservedParameterNames = new Set(mergedParameters.keys());
    for (const [param, value] of urlParameters) {
      if (this.#isFacetsParameter(param) || !preservedParameterNames.has(param)) {
        mergedParameters.append(param, value);
      }
    }

    return mergedParameters;
  }

  #usesLegacyTagPath() {
    const formURL = new URL(this.refs.facetsForm.action, window.location.origin);
    return this.hasAttribute('use-legacy-tag-path') && formURL.pathname.startsWith('/collections/');
  }

  /**
   * Returns product tags encoded after the current collection path.
   * @param {string} [pathname] - The pathname to parse
   * @returns {string[]} The active legacy tags
   */
  #getLegacyTagValues(pathname = window.location.pathname) {
    if (!this.#usesLegacyTagPath()) return [];

    const basePath = new URL(this.refs.facetsForm.action, window.location.origin).pathname.replace(/\/$/, '');
    const tagPathPrefix = `${basePath}/`;
    if (!pathname.startsWith(tagPathPrefix)) return [];

    return pathname
      .slice(tagPathPrefix.length)
      .split('+')
      .map((tag) => decodeURIComponent(tag))
      .filter(Boolean);
  }

  /**
   * Moves product tag filters from the query string into a localized collection path.
   * @param {URL} url - The URL to update
   * @param {URLSearchParams} urlParameters - The filter parameters
   * @param {string} [baseURL] - The unfiltered collection URL
   */
  #applyLegacyTagPath(url, urlParameters, baseURL = this.refs.facetsForm.action) {
    if (!this.#usesLegacyTagPath()) return;

    const tagValues = this.#getFilterValues(urlParameters, PRODUCT_TAG_FILTER);
    const basePath = new URL(baseURL, window.location.origin).pathname.replace(/\/$/, '');
    urlParameters.delete(PRODUCT_TAG_FILTER);
    url.pathname =
      tagValues.length > 0
        ? `${basePath}/${tagValues.map((tag) => encodeURIComponent(tag)).join('+')}`
        : basePath;
  }
  /**
   * Builds a filter URL, including collection tags encoded in the pathname.
   * @param {URLSearchParams} urlParameters - The filter parameters
   * @param {string} [baseURL] - The URL to update
   * @returns {URL} The filter URL
   */
  createFilterURL(urlParameters, baseURL = window.location.href) {
    const url = new URL(baseURL, window.location.origin);
    url.search = '';

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    this.#applyLegacyTagPath(url, url.searchParams);
    return url;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  #getResultsSectionId() {
    const results = document.querySelector('results-list[section-id]');
    const sectionId = results?.getAttribute('section-id');
    return sectionId || this.sectionId;
  }

  #getFacetRoot() {
    return this.closest('.collection-layout__facets');
  }

  #isDeferredFacetRefresh(resultsSectionId) {
    return resultsSectionId !== this.sectionId && Boolean(this.#getFacetRoot());
  }

  #getRequestSignature(urlParameters) {
    const parameters = new URLSearchParams(urlParameters);
    parameters.sort();
    return parameters.toString();
  }

  #isCurrentRequest(requestId, requestSignature) {
    const currentSignature = this.#getRequestSignature(new URL(window.location.href).searchParams);
    return requestId === this.#filterRequestId && requestSignature === currentSignature;
  }

  #setResultsPending(pending) {
    const results = document.querySelector('results-list');
    if (!results) return;

    results.toggleAttribute('data-pending', pending);

    const resultsList = results.querySelector('#ResultsList');
    resultsList?.toggleAttribute('inert', pending);
    resultsList?.setAttribute('aria-busy', pending ? 'true' : 'false');
  }

  #syncLocalFacetState(urlParameters) {
    const facetRoot = this.#getFacetRoot();
    if (!facetRoot) return;

    for (const input of facetRoot.querySelectorAll('input[name^="filter."]')) {
      if (!(input instanceof HTMLInputElement)) continue;

      if (input.type === 'checkbox') {
        input.checked = this.#getFilterValues(urlParameters, input.name).includes(input.value);
      } else if (input.matches('.price-facet__input')) {
        input.value = urlParameters.get(input.name) ?? '';
      }
    }

    for (const facetInputs of facetRoot.querySelectorAll('facet-inputs-component')) {
      const details = facetInputs.closest('details');
      const statusComponent = details?.querySelector('facet-status-component');

      if (statusComponent instanceof FacetStatusComponent) {
        statusComponent.updateListSummary(
          /** @type {HTMLInputElement[]} */ ([...facetInputs.querySelectorAll('input[type="checkbox"]:checked')])
        );
      }
    }

    for (const priceFacet of facetRoot.querySelectorAll('price-facet-component')) {
      if (priceFacet instanceof PriceFacetComponent) priceFacet.syncSummary();
    }
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash(urlParameters = this.createURLParameters(), pathname = window.location.pathname) {
    const url = new URL(window.location.href);
    url.pathname = pathname;
    url.hash = '';

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  #cancelResultsScroll = () => {
    this.#resultsScrollId += 1;

    const dialogComponent = this.closest('dialog-component');
    if (
      dialogComponent &&
      'setNextCloseScrollRestoration' in dialogComponent &&
      typeof dialogComponent.setNextCloseScrollRestoration === 'function'
    ) {
      dialogComponent.setNextCloseScrollRestoration(true);
    }
  };

  #scrollAfterResultsRender(renderPromise, scrollId) {
    void renderPromise.then(
      () => {
        if (scrollId !== this.#resultsScrollId) return;

        requestAnimationFrame(() => {
          if (scrollId !== this.#resultsScrollId) return;

          const resultsList = document.querySelector('results-list');
          if (
            resultsList &&
            'scrollToResults' in resultsList &&
            typeof resultsList.scrollToResults === 'function'
          ) {
            resultsList.scrollToResults();
          }
        });
      },
      (error) => console.error('[Facets] Failed to update results', error)
    );
  }

  #queueResultsScroll() {
    const dialogComponent = this.closest('dialog-component');

    if (!dialogComponent) {
      const renderPromise = this.#pendingSectionRender;
      if (renderPromise) this.#scrollAfterResultsRender(renderPromise, this.#queuedResultsScrollId);
      return;
    }

    if (
      'setNextCloseScrollRestoration' in dialogComponent &&
      typeof dialogComponent.setNextCloseScrollRestoration === 'function'
    ) {
      dialogComponent.setNextCloseScrollRestoration(false);
    }

    if (this.#scrollAfterDialogClose) return;

    this.#scrollAfterDialogClose = true;
    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        this.#scrollAfterDialogClose = false;

        const renderPromise = this.#pendingSectionRender;
        if (renderPromise) this.#scrollAfterResultsRender(renderPromise, this.#queuedResultsScrollId);
      },
      { once: true }
    );
  }

  /**
   * Updates filters and renders results before reconciling server-authoritative facets.
   * @param {URLSearchParams} [urlParameters] - The complete URL parameters for the requested state
   * @param {{ updateHistory?: boolean, scroll?: boolean, preservePage?: boolean }} [options] - Navigation behavior
   * @returns {Promise<void>}
   */
  updateFilters = (urlParameters = this.createURLParameters(), options = {}) => {
    const { updateHistory = true, scroll = true, preservePage = false } = options;
    urlParameters.delete('section_id');
    if (!preservePage) urlParameters.delete('page');
    this.#normalizeProductTagFilter(urlParameters);
    urlParameters.delete(REQUIRED_AVAILABILITY_FILTER);
    urlParameters.set(REQUIRED_AVAILABILITY_FILTER, REQUIRED_AVAILABILITY_VALUE);

    const localURLParameters = new URLSearchParams(urlParameters);
    const userURLParameters = new URLSearchParams(urlParameters);
    userURLParameters.delete(REQUIRED_AVAILABILITY_FILTER);
    const requestId = ++this.#filterRequestId;
    const resultsScrollId = ++this.#resultsScrollId;
    if (scroll) this.#queuedResultsScrollId = resultsScrollId;
    let requestURL = this.createFilterURL(urlParameters);

    if (updateHistory) {
      this.#updateURLHash(requestURL.searchParams, requestURL.pathname);
      requestURL = new URL(window.location.href);
    }

    const requestSignature = this.#getRequestSignature(requestURL.searchParams);

    this.#syncLocalFacetState(localURLParameters);
    this.#setResultsPending(true);
    this.dispatchEvent(new FilterUpdateEvent(userURLParameters));

    if (this.#isDeferredFacetRefresh(this.#getResultsSectionId())) {
      sectionRenderer.abortSection(this.sectionId);
    }

    const resultsRender = this.#updateSection(requestId, requestURL, requestSignature);
    this.#pendingSectionRender = resultsRender;
    if (scroll) this.#queueResultsScroll();
    return resultsRender.then((shouldRefreshFacets) => {
      if (shouldRefreshFacets) return this.#refreshFacets(requestId, requestURL, requestSignature);
    });
  };

  /**
   * Renders results immediately and reports whether facets need reconciliation after the results paint.
   * @param {number} requestId - The local filter request sequence
   * @param {URL} requestURL - The URL to render
   * @param {string} requestSignature - The normalized query parameters for this request
   * @returns {Promise<boolean>} Whether the deferred facets section should be refreshed
   */
  async #updateSection(requestId, requestURL, requestSignature) {
    const resultsSectionId = this.#getResultsSectionId();
    const deferFacets = this.#isDeferredFacetRefresh(resultsSectionId);
    try {
      const result = await sectionRenderer.renderSection(resultsSectionId, { url: requestURL });
      if (!this.#isCurrentRequest(requestId, requestSignature)) return false;
      if (!result) {
        this.#setResultsPending(false);
        return false;
      }

      const resultsList = document.querySelector('results-list');
      if (resultsList && 'updatePaginationLinks' in resultsList) {
        resultsList.updatePaginationLinks();
      }

      await new Promise((resolve) => requestAnimationFrame(resolve));
      if (!this.#isCurrentRequest(requestId, requestSignature)) return false;

      this.#setResultsPending(false);

      return deferFacets;
    } catch (error) {
      if (!this.#isCurrentRequest(requestId, requestSignature)) return false;

      this.#setResultsPending(false);
      console.error('[Facets] Failed to update results', error);
      return false;
    }
  }

  /**
   * Replaces the filter controls after fresh results have painted.
   * @param {number} requestId - The local filter request sequence
   * @param {URL} requestURL - The URL to render
   * @param {string} requestSignature - The normalized query parameters for this request
   */
  async #refreshFacets(requestId, requestURL, requestSignature) {
    try {
      await sectionRenderer.renderSection(this.sectionId, { url: requestURL });
      if (!this.#isCurrentRequest(requestId, requestSignature)) return;

      for (const facetsForm of document.querySelectorAll('facets-form-component')) {
        if (facetsForm instanceof FacetsFormComponent) {
          facetsForm.updateCollectionNavigationLinks();
          facetsForm.renderUnavailableActiveFilters();
        }
      }
    } catch (error) {
      if (!this.#isCurrentRequest(requestId, requestSignature)) return;

      console.error('[Facets] Failed to refresh filters', error);
    }

  }


  #createCollectionNavigationURL(collectionURL) {
    const url = new URL(collectionURL, window.location.origin);
    const currentURL = new URL(window.location.href);

    for (const param of new Set(currentURL.searchParams.keys())) {
      if (this.#isFacetsParameter(param)) continue;

      url.searchParams.delete(param);
      for (const value of currentURL.searchParams.getAll(param)) {
        url.searchParams.append(param, value);
      }
    }

    const facetParameters = this.#getFacetParameters();
    this.#applyLegacyTagPath(url, facetParameters, collectionURL);

    for (const [key, value] of facetParameters.entries()) {
      url.searchParams.append(key, value);
    }

    const filtersDrawer = this.closest('#filters-drawer');
    const openParameter = filtersDrawer instanceof HTMLElement ? filtersDrawer.dataset.openParameter : undefined;

    if (isMobileBreakpoint() && openParameter) {
      url.searchParams.set(openParameter, '1');
    }

    return url;
  }

  updateCollectionNavigationLinks() {
    const collectionLinks = this.querySelectorAll('[data-collection-link]');
    if (!collectionLinks.length) return;

    for (const collectionLink of collectionLinks) {
      if (!(collectionLink instanceof HTMLAnchorElement)) continue;

      const collectionURL = collectionLink.dataset.collectionUrl;
      if (!collectionURL) continue;

      collectionLink.href = this.#createCollectionNavigationURL(collectionURL).toString();
    }
  }

  renderUnavailableActiveFilters() {
    const removalsContainer = this.querySelector('[data-unavailable-filter-removals]');
    const pillTemplate = this.querySelector('[data-unavailable-filter-pill-template]');
    if (!(removalsContainer instanceof HTMLElement) || !(pillTemplate instanceof HTMLTemplateElement)) return;
    for (const unavailableValue of this.querySelectorAll('[data-unavailable-filter-value]')) unavailableValue.remove();
    if (!this.hasAttribute('use-legacy-tag-path')) {
      removalsContainer.replaceChildren();
      return;
    }

    const currentURL = new URL(window.location.href);
    const pills = [];

    for (const param of new Set(currentURL.searchParams.keys())) {
      if (!param.startsWith('filter.') || param === REQUIRED_AVAILABILITY_FILTER) continue;

      const values = this.#getFilterValues(currentURL.searchParams, param);
      for (const value of values) {
        const valueIsAvailable = Array.from(this.refs.facetsForm.elements).some((element) => {
          if (!(element instanceof HTMLInputElement || element instanceof HTMLSelectElement)) return false;
          if (element.name !== param) return false;
          if (element instanceof HTMLInputElement && element.type === 'number') return true;
          return element.value === value || element.dataset.label === value;
        });
        if (valueIsAvailable) continue;

        const pill = pillTemplate.content.firstElementChild?.cloneNode(true);
        if (!(pill instanceof HTMLElement)) continue;

        const removalURL = new URL(currentURL);
        const remainingValues = values.filter((currentValue) => currentValue !== value);
        removalURL.searchParams.delete(param);
        if (param === PRODUCT_TAG_FILTER) {
          if (remainingValues.length > 0) removalURL.searchParams.set(param, remainingValues.join(','));
        } else {
          for (const remainingValue of remainingValues) removalURL.searchParams.append(param, remainingValue);
        }
        removalURL.searchParams.delete('page');
        pill.dataset.url = removalURL.toString();

        const filterParamName = param.replaceAll('.', '-');
        const facet = this.querySelector(`[data-filter-param-name="${CSS.escape(filterParamName)}"]`);
        const filterLabel = facet?.querySelector('.facets__label')?.textContent;
        const label = pill.querySelector('[data-unavailable-filter-label]');
        if (label) {
          label.textContent = filterLabel?.trim() ? `${filterLabel.trim()}: ${value}` : value;
        }

        const valueTemplate = facet?.querySelector('[data-unavailable-filter-value-template]');
        const valuesList = facet?.querySelector('.facets__inputs-list');
        const unavailableValue =
          valueTemplate instanceof HTMLTemplateElement ? valueTemplate.content.firstElementChild?.cloneNode(true) : null;
        if (unavailableValue instanceof HTMLElement && valuesList instanceof HTMLElement) {
          const input = unavailableValue.querySelector('input');
          const inputLabel = unavailableValue.querySelector('label');
          const valueLabel = unavailableValue.querySelector('[data-unavailable-filter-value-label]');
          if (input instanceof HTMLInputElement && inputLabel instanceof HTMLLabelElement && valueLabel) {
            const inputId = `Unavailable-filter-${crypto.randomUUID()}`;
            input.id = inputId;
            input.name = param;
            input.value = value;
            input.dataset.label = value;
            input.dataset.unavailableFilter = '';
            input.dataset.urlToRemove = removalURL.toString();
            inputLabel.htmlFor = inputId;
            valueLabel.textContent = value;
            valuesList.prepend(unavailableValue);

            facet?.setAttribute('open-by-default-on-mobile', '');
            facet?.setAttribute('open-by-default-on-desktop', '');
            const details = facet?.querySelector('details');
            if (details instanceof HTMLDetailsElement) {
              details.open = true;
              details.setAttribute('declarative-open', '');
            }
          }
        }

        pills.push(pill);
      }
    }

    removalsContainer.replaceChildren(...pills);

    for (const facet of this.querySelectorAll('[data-filter-param-name]:has([data-unavailable-filter-value])')) {
      const checkedInputs = facet.querySelectorAll('input[type="checkbox"]:checked');
      const facetStatus = facet.querySelector('facet-status-component [ref="facetStatus"]');
      if (!(facetStatus instanceof HTMLElement)) continue;

      facetStatus.textContent = checkedInputs.length.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
    }
  }

  #handleCollectionNavigationClick = (event) => {
    const collectionLink = event.target instanceof Element ? event.target.closest('[data-collection-link]') : null;

    if (!(collectionLink instanceof HTMLAnchorElement)) return;

    event.preventDefault();

    const collectionURL = collectionLink.dataset.collectionUrl;
    if (!collectionURL) {
      window.location.assign(collectionLink.href);
      return;
    }

    window.location.assign(this.#createCollectionNavigationURL(collectionURL).toString());
  };

  #getCollectionNavigationFacetValue(param, value) {
    if (!value.startsWith(FILTER_SETTING_GROUP_PREFIX)) return value;

    for (const element of this.refs.facetsForm.elements) {
      if (!(element instanceof HTMLInputElement)) continue;
      if (element.name !== param || element.value !== value) continue;

      const label = element.dataset.label?.trim();
      if (label) return label;
    }

    return value;
  }

  #getFacetParameters() {
    const url = new URL(window.location.href);
    const facetParameters = new URLSearchParams();

    for (const [key, value] of url.searchParams.entries()) {
      if (key.startsWith('filter.')) {
        facetParameters.append(key, this.#getCollectionNavigationFacetValue(key, value));
      }
    }
    if (this.#usesLegacyTagPath()) {
      const legacyTags = this.#getLegacyTagValues();
      if (legacyTags.length > 0) facetParameters.set(PRODUCT_TAG_FILTER, legacyTags.join(','));
    }

    return facetParameters;
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   * @param {{ updateHistory?: boolean, scroll?: boolean, preservePage?: boolean }} [options] - Navigation behavior
   * @returns {Promise<void>}
   */
  updateFiltersByURL(url, options) {
    const targetURL = new URL(url, window.location.origin);
    const urlParameters = this.#mergeURLParameters(targetURL.searchParams);

    if (this.#usesLegacyTagPath()) {
      const legacyTags = this.#getLegacyTagValues(targetURL.pathname);
      if (legacyTags.length > 0) urlParameters.set(PRODUCT_TAG_FILTER, legacyTags.join(','));
    }

    return this.updateFilters(urlParameters, options);
  }

}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   * @param {Event} [event] - The filter input change event
   */
  updateFilters(event) {
    if (event?.target instanceof HTMLInputElement && this.#navigateFromUnavailableFilter(event.target)) return;

    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      if (this.#navigateFromUnavailableFilter(closestInput)) return;
      this.updateFilters();
    }
  }

  #navigateFromUnavailableFilter(input) {
    const url = input.dataset.urlToRemove;
    if (!input.hasAttribute('data-unavailable-filter') || input.checked || !url) return false;

    window.location.assign(url);
    return true;
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (inputElement.checked) {
      const remainingValues = formData.getAll(inputElement.name).filter((value) => value !== inputElement.value);
      formData.delete(inputElement.name);
      for (const value of remainingValues) formData.append(inputElement.name, value);
    } else {
      formData.append(inputElement.name, inputElement.value);
    }

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);
    const url = facetsForm.createFilterURL(urlParameters, window.location.pathname);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

class QuickFiltersComponent extends Component {
  #activeInputId = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('pointerdown', this.#handlePointerDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('pointerdown', this.#handlePointerDown);
  }

  #getFacetsForm() {
    const facetsForm = document.querySelector('facets-form-component[data-primary-facets-form]');
    return facetsForm instanceof FacetsFormComponent ? facetsForm : null;
  }

  updateFilters() {
    const selectedInput = this.querySelector('[data-quick-filter-input]:checked');
    if (!(selectedInput instanceof HTMLInputElement)) return;

    const facetsForm = this.#getFacetsForm();
    if (!facetsForm) return;

    const urlParameters = facetsForm.createURLParameters();
    urlParameters.set(selectedInput.name, selectedInput.value);
    facetsForm.updateFilters(urlParameters);
  }

  #handlePointerDown = (event) => {
    if (!(event.target instanceof Element)) return;

    const label = event.target.closest('label[for]');
    const input = event.target.closest('[data-quick-filter-input]');

    if (input instanceof HTMLInputElement) {
      this.#activeInputId = input.checked ? input.id : null;
      return;
    }

    if (!(label instanceof HTMLLabelElement)) {
      this.#activeInputId = null;
      return;
    }

    const inputId = label.getAttribute('for');
    if (!inputId) {
      this.#activeInputId = null;
      return;
    }

    const linkedInput = this.querySelector(`#${CSS.escape(inputId)}`);
    this.#activeInputId = linkedInput instanceof HTMLInputElement && linkedInput.checked ? linkedInput.id : null;
  };

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const clickedInput = event.target.closest('[data-quick-filter-input]');
    const clickedLabel = event.target.closest('label[for]');

    let input = null;

    if (clickedInput instanceof HTMLInputElement) {
      input = clickedInput;
    } else if (clickedLabel instanceof HTMLLabelElement) {
      const inputId = clickedLabel.getAttribute('for');
      if (!inputId) return;

      input = this.querySelector(`#${CSS.escape(inputId)}`);
    }

    if (!(input instanceof HTMLInputElement) || input.disabled) return;

    const wasActive = this.#activeInputId != null && this.#activeInputId === input.id;
    this.#activeInputId = null;

    if (!wasActive) return;

    const removeURL = input.dataset.removeUrl;
    if (!removeURL) return;

    event.preventDefault();

    const facetsForm = this.#getFacetsForm();
    if (!facetsForm) return;

    facetsForm.updateFiltersByURL(removeURL);
  };

  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.closest('.facets__pill-wrapper')?.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();

      if (closestInput.checked) {
        const removeURL = closestInput.dataset.removeUrl;
        if (!removeURL) return;

        const facetsForm = this.#getFacetsForm();
        if (!facetsForm) return;

        facetsForm.updateFiltersByURL(removeURL);
        return;
      }

      closestInput.checked = true;
      this.updateFilters();
    }
  }
}

if (!customElements.get('quick-filters-component')) {
  customElements.define('quick-filters-component', QuickFiltersComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {string} */
  currency;
  /** @type {string} */
  moneyFormat;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.currency = this.dataset.currency ?? 'USD';
    this.moneyFormat = this.#extractMoneyPlaceholder(this.dataset.moneyFormat ?? '{{amount}}');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Extracts the placeholder from a money format string, removing currency symbols.
   * @param {string} format - The money format (e.g., "${{amount}}", "{{amount}} USD")
   * @returns {string} Just the placeholder (e.g., "{{amount}}")
   */
  #extractMoneyPlaceholder(format) {
    const match = format.match(/{{\s*\w+\s*}}/);
    return match ? match[0] : '{{amount}}';
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.syncSummary();
  }

  /**
   * Synchronizes the price-input constraints and selected-value summary.
   */
  syncSummary() {
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Parses a formatted money value into minor units
   * displayValue can come from user input or API response
   * @param {string} displayValue - The display value (e.g., "10.50" for USD, "9,50" for EUR, "1000" for JPY)
   * @param {string} currency - The currency code
   * @returns {number} The value in minor units
   */
  #parseDisplayValue(displayValue, currency) {
    return convertMoneyToMinorUnits(displayValue, currency) ?? 0;
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const { currency, moneyFormat } = this;
    // Parse the user's input value using currency-aware parsing
    const value = this.#parseDisplayValue(input.value, currency);

    // data-min and data-max now contain raw minor unit values (not formatted)
    const min = this.#parseDisplayValue(input.getAttribute('data-min') ?? '0', currency);
    const max = this.#parseDisplayValue(input.getAttribute('data-max') ?? '0', currency);

    if (value < min) {
      input.value = formatMoney(min, moneyFormat, currency);
    } else if (value > max) {
      input.value = formatMoney(max, moneyFormat, currency);
    }
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const filterTag = this.dataset.filterTag;
    if (filterTag) {
      const urlParameters = facetsForm.createURLParameters();
      const remainingTags = urlParameters
        .getAll(PRODUCT_TAG_FILTER)
        .flatMap((value) => value.split(','))
        .filter((value) => value && value !== filterTag);

      urlParameters.delete(PRODUCT_TAG_FILTER);
      if (remainingTags.length > 0) urlParameters.set(PRODUCT_TAG_FILTER, remainingTags.join(','));
      facetsForm.updateFilters(urlParameters);
      return;
    }

    const url = this.dataset.url;
    if (!url) return;
    if (this.hasAttribute('data-full-page-navigation')) {
      window.location.assign(url);
      return;
    }
    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const activeClass = this.getAttribute('active-class') || 'active';
      clearButton.classList.toggle(activeClass, event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = [];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;
    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();

    const dialogComponent = this.closest('dialog-component');
    if (dialogComponent && 'closeDialog' in dialogComponent) {
      dialogComponent.closeDialog();
    }
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const currency = facetStatus.dataset.currency || '';
    const minInputNum = this.#parseCents(minInputValue, '0', currency);
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax, currency);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as minor units (cents for most currencies, but adjusted for zero-decimal currencies)
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid (formatted string like "11,400")
   * @param {string} currency - The currency code (e.g., 'USD', 'JPY', 'KRW')
   * @returns {number} The money value in minor units
   */
  #parseCents(value, fallback = '0', currency = '') {
    // Try to parse the value
    const result = convertMoneyToMinorUnits(value, currency);
    if (result !== null) return result;

    // Fall back to parsing the fallback string (which may have formatting like "11,400")
    const fallbackResult = convertMoneyToMinorUnits(fallback, currency);
    if (fallbackResult !== null) return fallbackResult;

    // Last resort: clean and parse as integer
    const cleanFallback = fallback.replace(/[^\d]/g, '');
    return parseInt(cleanFallback, 10) || 0;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const format = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return formatMoney(moneyValue, format, currency);
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}
